import { Platform } from 'react-native';

/**
 * CORE-1733 — общие фоны платформы v4: единственный источник значений.
 *
 * Статья Confluence 110756133 «Agapella Web — общие фоны и поверхности · тёмная
 * и светлая темы» (светлая v4 от 16.10.2026), раздел «Единый CSS». Слои, стопы,
 * направления и прозрачности — дословно; первый слой рисуется поверх остальных.
 *
 * Три уровня, которые нельзя смешивать (статья):
 *   1. фон страницы — непрерывная основа: неподвижный слой размером с окно
 *      (`body::before` в public/global.css); открытые области остаются на нём;
 *   2. поверхность — локальная глубина карточки, поля, панели: свой градиент в
 *      собственных координатах и контур 1px; непрозрачные поверхности не
 *      вкладываются друг в друга;
 *   3. акцент состояния (выбранность, focus, CTA) — у компонентов, не здесь.
 *
 * Слой страницы обязан существовать до JS, поэтому его значения продублированы
 * токенами в public/global.css; равенство держит тест (platformBackgroundV4).
 * Live берёт страницу, шапку и поверхность отсюда же (styles/liveTokens.ts).
 */
export type PlatformTheme = 'dark' | 'light';

export interface PlatformBackgrounds {
  /** Непрозрачная основа под слоями страницы (`--bg-base`). */
  pageBase: string;
  /** Слои фона страницы (`--bg-page-image`), viewport-координаты. */
  pageImage: string;
  /** Непрозрачный цвет шапки — подложка под её градиентом. */
  headerBase: string;
  headerImage: string;
  /** Нижняя линия шапки, 1px. */
  headerBorder: string;
  /** Сплошной цвет поверхности — там, где градиент не рисуется (натив, мелкие заливки). */
  surfaceBase: string;
  /** Градиент поверхности в её собственных координатах. */
  surfaceImage: string;
  surfaceBorder: string;
  /** Центральная молочная поверхность (`theme-surface--central`): одна главная карточка экрана. */
  centralBase: string;
  centralImage: string;
}

const dark: PlatformBackgrounds = {
  pageBase: '#202226',
  pageImage:
    'radial-gradient(ellipse at 4% 60%,rgba(233,130,69,.065),transparent 53%),radial-gradient(ellipse at 94% 8%,rgba(215,183,164,.045),transparent 56%)',
  headerBase: '#35424B',
  headerImage: 'linear-gradient(110deg, #46535A 0%, #35424B 52%, #303C45 100%)',
  headerBorder: '#65716F',
  surfaceBase: '#292B2E',
  surfaceImage:
    'radial-gradient(ellipse at 100% 100%,rgba(233,130,69,.09),transparent 60%),linear-gradient(135deg,#292B2E 0%,#25272A 52%,#302D2B 100%)',
  surfaceBorder: 'rgba(241,181,142,.20)',
  // Статья задаёт центральную поверхность только для светлой темы; в тёмной
  // центральная карточка — обычная поверхность.
  centralBase: '#292B2E',
  centralImage:
    'radial-gradient(ellipse at 100% 100%,rgba(233,130,69,.09),transparent 60%),linear-gradient(135deg,#292B2E 0%,#25272A 52%,#302D2B 100%)',
};

const light: PlatformBackgrounds = {
  pageBase: '#FCF5E7',
  pageImage:
    'radial-gradient(ellipse at 47% 28%,#FFFCF4 0%,rgba(255,252,244,.72) 28%,rgba(255,252,244,0) 68%),'
    + 'radial-gradient(ellipse at 0% 55%,rgba(221,190,145,.16) 0%,rgba(221,190,145,0) 60%),'
    + 'radial-gradient(ellipse at 100% 100%,rgba(231,200,156,.12) 0%,rgba(231,200,156,0) 58%),'
    + 'linear-gradient(125deg,#F5E8D1 0%,#FCF5E7 44%,#F6EBD8 100%)',
  // Шапка светлой темы в статье — только градиент; подложка — его средний стоп.
  headerBase: '#FFFCF4',
  headerImage: 'linear-gradient(110deg,#F7ECD9 0%,#FFFCF4 48%,#F9EFDF 100%)',
  headerBorder: 'rgba(226,151,105,.12)',
  surfaceBase: '#FFFCF3',
  surfaceImage:
    'radial-gradient(ellipse at 100% 100%,rgba(212,167,103,.03),transparent 65%),linear-gradient(135deg,#F7ECD9 0%,#FFFCF3 46%,#F9EDDA 100%)',
  surfaceBorder: 'rgba(168,124,76,.15)',
  centralBase: '#FFFBEE',
  centralImage: 'linear-gradient(135deg,#F7E9D1 0%,#FFFBEE 45%,#F8ECD6 100%)',
};

export const platformBackgrounds: Record<PlatformTheme, PlatformBackgrounds> = { dark, light };

const WEB = Platform.OS === 'web';

/**
 * Фон страницы целиком на элементе размером с окно, который обязан быть
 * непрозрачным: полноэкранное меню, модальный маршрут поверх страницы. Такой
 * элемент совпадает с окном, поэтому слои в его координатах — те же, что у
 * `body::before`. Вне веба — сплошная основа.
 */
export function pageFill(theme: PlatformTheme): Record<string, unknown> {
  const b = platformBackgrounds[theme];
  return WEB
    ? { backgroundColor: b.pageBase, backgroundImage: b.pageImage, backgroundRepeat: 'no-repeat', backgroundSize: 'cover' }
    : { backgroundColor: b.pageBase };
}

/**
 * Полоса цвета страницы для липких элементов (полоса вкладок): они закрывают
 * прокручиваемое под собой, поэтому непрозрачны, но окно не покрывают — слои
 * в их собственных координатах легли бы иначе, чем вокруг, и дали бы шов.
 * `background-attachment: fixed` здесь не спасает: внутри ScrollView
 * react-native-web (transform) Chrome откатывает его к обычному. Основа v4 —
 * цвет самого слоя на большей части открытой страницы.
 */
export function pageBand(theme: PlatformTheme): Record<string, unknown> {
  return { backgroundColor: platformBackgrounds[theme].pageBase };
}

/** Фон шапки платформы (`theme-header`): градиент и нижняя линия 1px. */
export function headerFill(theme: PlatformTheme): Record<string, unknown> {
  const b = platformBackgrounds[theme];
  return WEB
    ? { backgroundColor: b.headerBase, backgroundImage: b.headerImage }
    : { backgroundColor: b.headerBase };
}

/**
 * Локальная поверхность (`theme-surface`, `--central` — центральная молочная):
 * градиент в собственных координатах и контур 1px. Не вкладывать одну
 * непрозрачную поверхность в другую — внутренние блоки остаются прозрачными.
 */
export function surfaceFill(theme: PlatformTheme, opts: { central?: boolean } = {}): Record<string, unknown> {
  const b = platformBackgrounds[theme];
  const base = opts.central ? b.centralBase : b.surfaceBase;
  const image = opts.central ? b.centralImage : b.surfaceImage;
  return {
    backgroundColor: base,
    ...(WEB ? { backgroundImage: image } : null),
    borderWidth: 1,
    borderColor: b.surfaceBorder,
  };
}
